// Election manager — handles applications + roster updates for the elite
// titles (Noble / Military / Clergy). Phase 5.2.1 fills empty seats with NPC
// placeholders; when real multiplayer arrives in 5.2.2 NPCs get replaced with
// real-player entries fetched from the backend.
//
// Rule (from the design call): "Open seat → highest-wealth qualified applicant
// fills it." So an apply() either takes an open seat OR displaces the
// lowest-wealth incumbent if the player outbids them.

import { Events, emit, on } from '../ui/eventBus.js'
import {
  ElectedTitleIds,
  ElectionConfig,
  KingdomOrder,
  NpcNamesByKingdom,
  DegreeTierOrder,
} from '../config/balance.js'
import { state, persist, getProfile } from './state.js'
import * as season from './seasonManager.js'
import * as profile from './profileManager.js'

export function init() {
  for (const id of KingdomOrder) seedRoster(id)
  persist()
  on(Events.KingdomChange, () => emit(Events.ProfileChange, { profile: getProfile() }))
}

function seedRoster(kingdomId) {
  const k = state.kingdoms[kingdomId]
  if (!k.rosters) k.rosters = {}
  for (const titleId of ElectedTitleIds) {
    if (k.rosters[titleId]) continue
    const cap = ElectionConfig[titleId].cap
    const names = NpcNamesByKingdom[kingdomId] ?? []
    k.rosters[titleId] = Array.from({ length: cap }, (_, i) => ({
      id: `npc-${kingdomId}-${titleId}-${i}`,
      name: names[i % names.length] ?? `Cat ${i + 1}`,
      // NPC wealth scaled by title — Nobles richest, then Military, then Clergy.
      wealth: baseNpcWealthFor(titleId) + Math.floor(Math.random() * 20_000),
      isPlayer: false,
    }))
  }
}

function baseNpcWealthFor(titleId) {
  if (titleId === 'NOBLE') return 60_000
  if (titleId === 'MILITARY') return 35_000
  return 25_000 // CLERGY
}

export function getRoster(kingdomId, titleId) {
  return state.kingdoms[kingdomId]?.rosters?.[titleId] ?? []
}

// Eligibility for a title (CR-6 degree model). `degrees` maps subject → highest
// tier (CERT < MASTERS < PHD). A subject qualifies for `minTier` when its tier
// index is at or above the required one.
export function meetsPrereqs(titleId) {
  const cfg = ElectionConfig[titleId]
  if (!cfg) return false
  const degrees = getProfile().degrees ?? {}
  const minIdx = DegreeTierOrder.indexOf(cfg.minTier)
  const tierIdx = (subject) => DegreeTierOrder.indexOf(degrees[subject])
  if (cfg.requiredSubject) {
    return tierIdx(cfg.requiredSubject) >= minIdx
  }
  if (cfg.requiredSubjectOneOf) {
    return cfg.requiredSubjectOneOf.some((s) => tierIdx(s) >= minIdx)
  }
  // No specific subject — any subject at minTier+ qualifies.
  return Object.keys(degrees).some((s) => tierIdx(s) >= minIdx)
}

// Wealth needed to enter the roster RIGHT NOW. If there's an empty seat, any
// amount qualifies (returns 0). Otherwise returns the lowest incumbent's
// wealth + 1m — the threshold to displace them.
export function thresholdToClaim(titleId) {
  const kingdomId = getProfile()?.homeKingdom ?? state.activeKingdom
  const roster = getRoster(kingdomId, titleId)
  const cap = ElectionConfig[titleId].cap
  if (roster.length < cap) return 0
  const wealths = roster.map(effectiveWealth)
  return Math.min(...wealths) + 1
}

function effectiveWealth(holder) {
  return holder.isPlayer ? season.getWealth() : holder.wealth
}

// Player applies for a seat in the named title. Validates prereqs + wealth
// threshold; on success either takes an empty seat or displaces the lowest
// incumbent.
export function apply(titleId) {
  if (!meetsPrereqs(titleId)) {
    emit(Events.ElectionLost, { titleId, reason: 'prereqs' })
    return false
  }
  const p = getProfile()
  const kingdomId = p.homeKingdom ?? state.activeKingdom
  const roster = getRoster(kingdomId, titleId)
  const cap = ElectionConfig[titleId].cap
  const myWealth = season.getWealth()

  // Already on this roster? No-op.
  if (roster.some((h) => h?.isPlayer)) {
    emit(Events.ElectionLost, { titleId, reason: 'already-seated' })
    return false
  }

  const playerEntry = { id: 'player', name: p.catName || 'You', isPlayer: true }

  if (roster.length < cap) {
    roster.push(playerEntry)
    profile.elevateTitle(titleId)
    persist()
    emit(Events.ElectionWon, { titleId, displaced: null })
    return true
  }

  // Find lowest-wealth incumbent.
  let lowestIdx = 0
  let lowestWealth = effectiveWealth(roster[0])
  for (let i = 1; i < roster.length; i++) {
    const w = effectiveWealth(roster[i])
    if (w < lowestWealth) {
      lowestWealth = w
      lowestIdx = i
    }
  }
  if (myWealth <= lowestWealth) {
    emit(Events.ElectionLost, { titleId, reason: 'wealth', threshold: lowestWealth })
    return false
  }
  const displaced = roster[lowestIdx]
  roster[lowestIdx] = playerEntry
  profile.elevateTitle(titleId)
  persist()
  emit(Events.ElectionWon, { titleId, displaced })
  return true
}

// Player resigns from their elected seat and falls back to their starting
// (Common-class) title.
export function resign() {
  const p = getProfile()
  const kingdomId = p.homeKingdom ?? state.activeKingdom
  for (const titleId of ElectedTitleIds) {
    const roster = state.kingdoms[kingdomId]?.rosters?.[titleId]
    if (!roster) continue
    const idx = roster.findIndex((h) => h?.isPlayer)
    if (idx >= 0) {
      roster.splice(idx, 1)
      profile.demoteFromElected()
      persist()
      emit(Events.ElectionResigned, { titleId })
      return true
    }
  }
  return false
}

export function playerHoldsElectedTitle() {
  const p = getProfile()
  const kingdomId = p.homeKingdom
  if (!kingdomId) return null
  for (const titleId of ElectedTitleIds) {
    const roster = getRoster(kingdomId, titleId)
    if (roster.some((h) => h?.isPlayer)) return titleId
  }
  return null
}
